import { useState, useMemo } from "react";
import { useCart } from "../../context/CartContext";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import countries from "../../countries.json";

// Shipping rates by region (all in USD)
const SHIPPING_RATES = {
  us: {
    standard: 15,
    express: 35,
    deliveryTime: { standard: "7-14 Business Days", express: "3-5 Business Days" }
  },
  eu: {
    standard: 12,
    express: 30,
    deliveryTime: { standard: "7-14 Business Days", express: "3-5 Business Days" }
  },
  uk: {
    standard: 12,
    express: 28,
    deliveryTime: { standard: "7-14 Business Days", express: "3-5 Business Days" }
  },
  asia: {
    standard: 18,
    express: 40,
    deliveryTime: { standard: "7-14 Business Days", express: "3-5 Business Days" }
  },
  row: {
    standard: 60,
    express: 60,
    deliveryTime: { standard: "14-21 Business Days", express: "Up to 7 Days" }
  }
};

// Country to region mapping
const COUNTRY_REGIONS = {
  "United States": "us",
  "USA": "us",
  "United Kingdom": "uk",
  "UK": "uk",
  "Germany": "eu",
  "France": "eu",
  "Italy": "eu",
  "Spain": "eu",
  "Netherlands": "eu",
  "Belgium": "eu",
  "Austria": "eu",
  "Poland": "eu",
  "Sweden": "eu",
  "Denmark": "eu",
  "Finland": "eu",
  "Ireland": "eu",
  "Portugal": "eu",
  "Greece": "eu",
  "Czech Republic": "eu",
  "Romania": "eu",
  "Hungary": "eu",
  "Slovakia": "eu",
  "Bulgaria": "eu",
  "Croatia": "eu",
  "Slovenia": "eu",
  "Lithuania": "eu",
  "Latvia": "eu",
  "Estonia": "eu",
  "Luxembourg": "eu",
  "Malta": "eu",
  "Cyprus": "eu",
  "Japan": "asia",
  "South Korea": "asia",
  "China": "asia",
  "Hong Kong": "asia",
  "Taiwan": "asia",
  "Singapore": "asia",
  "Malaysia": "asia",
  "Thailand": "asia",
  "Vietnam": "asia",
  "Philippines": "asia",
  "Indonesia": "asia",
  "Australia": "asia",
  "New Zealand": "asia",
  "India": "asia",
};

const getRegion = (country) => COUNTRY_REGIONS[country] || "row";
const getShippingCost = (country, method) => {
  const rates = SHIPPING_RATES[getRegion(country)];
  return rates[method] || rates.standard;
};

export default function CheckoutPage() {
  const { cartItems } = useCart();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    city: "",
    state: "",
    zip: "",
    street: "",
    note: "",
    currency: "USD"
  });

  const [shippingMethod, setShippingMethod] = useState("standard");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculations
  const subtotal = useMemo(() => 
    cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0), 
    [cartItems]
  );

  const shippingCost = useMemo(() => 
    form.country ? getShippingCost(form.country, shippingMethod) : 0, 
    [form.country, shippingMethod]
  );

  const total = subtotal + shippingCost;

  const deliveryTime = useMemo(() => {
    if (!form.country) return null;
    return SHIPPING_RATES[getRegion(form.country)].deliveryTime[shippingMethod];
  }, [form.country, shippingMethod]);

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!form.phone.trim()) newErrors.phone = "Phone is required";
    if (!form.country) newErrors.country = "Country is required";
    if (!form.city.trim()) newErrors.city = "City is required";
    if (!form.state.trim()) newErrors.state = "State is required";
    if (!form.zip.trim()) newErrors.zip = "Zip code is required";
    if (!form.street.trim()) newErrors.street = "Address is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill all required fields");
      return;
    }

    if (cartItems.length === 0) {
      toast.error("Your cart is empty");
      navigate("/cart");
      return;
    }

    setIsSubmitting(true);

    try {
      const orderRes = await axios.post("https://admin.bodiosyakdown.com/api/orders", {
        ...form,
        items: cartItems,
        shippingMethod,
        shippingCost,
        subtotal,
        total
      });

      const orderId = orderRes.data.orderId;

      const res = await axios.post("https://admin.bodiosyakdown.com/api/golomt/payment", {
        amount: total.toFixed(2),
        currency: form.currency,
        orderId,
        description: `Order #${orderId}`,
      }, { withCredentials: true });

      if (res.data.success && res.data.redirectUrl) {
        window.location.href = res.data.redirectUrl;
      } else {
        toast.error("Payment link not received");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      toast.error("Payment failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Input styles
  const inputClass = (field) => `w-full border px-4 py-3 rounded-lg text-sm focus:outline-none focus:ring-2 transition-all ${
    errors[field] 
      ? "border-red-400 focus:ring-red-200" 
      : "border-gray-200 focus:ring-black/5 focus:border-gray-400"
  }`;

  return (
    <div className="min-h-screen bg-[#F7F4EF] py-12 px-4 mt-20 font-libreFranklin">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-light text-center mb-8">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 md:p-8 space-y-6">
              
              {/* Contact Information */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-100">
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name<span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className={inputClass("name")}
                    />
                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email<span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className={inputClass("email")}
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone<span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+1 234 567 8900"
                      className={inputClass("phone")}
                    />
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-100">
                  Shipping Address
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Country/Region<span className="text-red-500 ml-1">*</span>
                    </label>
                    <select
                      name="country"
                      value={form.country}
                      onChange={handleChange}
                      className={inputClass("country")}
                    >
                      <option value="">Select Country</option>
                      {countries.map((country, idx) => (
                        <option key={idx} value={country}>{country}</option>
                      ))}
                    </select>
                    {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Street Address<span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      name="street"
                      value={form.street}
                      onChange={handleChange}
                      placeholder="123 Main St, Apt 4B"
                      className={inputClass("street")}
                    />
                    {errors.street && <p className="text-red-500 text-xs mt-1">{errors.street}</p>}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City<span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={form.city}
                        onChange={handleChange}
                        placeholder="New York"
                        className={inputClass("city")}
                      />
                      {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State/Province<span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={form.state}
                        onChange={handleChange}
                        placeholder="NY"
                        className={inputClass("state")}
                      />
                      {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Zip/Postal<span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="text"
                        name="zip"
                        value={form.zip}
                        onChange={handleChange}
                        placeholder="10001"
                        className={inputClass("zip")}
                      />
                      {errors.zip && <p className="text-red-500 text-xs mt-1">{errors.zip}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipping Method */}
              {form.country && (
                <div>
                  <h2 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-100">
                    Shipping Method
                  </h2>
                  <div className="space-y-3">
                    <label className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-all ${
                      shippingMethod === "standard" ? "border-black bg-gray-50" : "border-gray-200 hover:border-gray-300"
                    }`}>
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="shippingMethod"
                          value="standard"
                          checked={shippingMethod === "standard"}
                          onChange={(e) => setShippingMethod(e.target.value)}
                          className="w-4 h-4 text-black"
                        />
                        <div>
                          <p className="font-medium text-gray-900">Standard Shipping</p>
                          <p className="text-sm text-gray-500">
                            {SHIPPING_RATES[getRegion(form.country)].deliveryTime.standard}
                          </p>
                        </div>
                      </div>
                      <span className="font-medium">${SHIPPING_RATES[getRegion(form.country)].standard}</span>
                    </label>

                    <label className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-all ${
                      shippingMethod === "express" ? "border-black bg-gray-50" : "border-gray-200 hover:border-gray-300"
                    }`}>
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="shippingMethod"
                          value="express"
                          checked={shippingMethod === "express"}
                          onChange={(e) => setShippingMethod(e.target.value)}
                          className="w-4 h-4 text-black"
                        />
                        <div>
                          <p className="font-medium text-gray-900">Express Shipping</p>
                          <p className="text-sm text-gray-500">
                            {SHIPPING_RATES[getRegion(form.country)].deliveryTime.express}
                          </p>
                        </div>
                      </div>
                      <span className="font-medium">${SHIPPING_RATES[getRegion(form.country)].express}</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Note */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Order Note <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <textarea
                  name="note"
                  value={form.note}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Any special instructions..."
                  className="w-full border border-gray-200 px-4 py-3 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/5 focus:border-gray-400 resize-none"
                />
              </div>

              {/* Mobile Submit */}
              <button
                type="submit"
                disabled={isSubmitting || cartItems.length === 0}
                className="lg:hidden w-full bg-black text-white py-4 rounded-lg font-medium hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : `Pay $${total.toFixed(2)}`}
              </button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-28">
              <h2 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-100">
                Order Summary
              </h2>

              <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                {cartItems.map((item, idx) => (
                  <div key={idx} className="flex gap-3">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {item.image && <img src={item.image} alt={item.title} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
                      <p className="text-xs text-gray-500">
                        {item.color && `${item.color} / `}{item.size} × {item.quantity}
                      </p>
                      <p className="text-sm font-medium">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-3 pt-4 border-t border-gray-100">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">${subtotal.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-medium">
                    {form.country ? `$${shippingCost.toFixed(2)}` : <span className="text-gray-400">Select country</span>}
                  </span>
                </div>

                {deliveryTime && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Delivery</span>
                    <span className="text-gray-500">{deliveryTime}</span>
                  </div>
                )}

                <div className="flex justify-between text-lg font-medium pt-3 border-t border-gray-100">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>

              {/* Desktop Submit */}
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={isSubmitting || cartItems.length === 0}
                className="hidden lg:flex w-full mt-6 bg-black text-white py-4 rounded-lg font-medium hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : `Place Order • $${total.toFixed(2)}`}
              </button>

              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Secure checkout powered by Golomt Bank
              </div>
            </div>

            <a 
              href="/shipping-policy" 
              className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="underline underline-offset-2">Learn about our shipping policy</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}